import _Divider from "@mui/material/Divider";
import _CircularProgress from "@mui/material/CircularProgress";
import _Button from "@mui/material/Button";
import _Box from "@mui/material/Box";
import _Typography from "@mui/material/Typography";
import _IconButton from "@mui/material/IconButton";
import _ChevronLeft from "@mui/icons-material/ChevronLeft";
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import emptyStateIllustration from '../../../assets/images/find-illus.svg';
import { useFileSaver } from '../../../hooks';
import { useAmplitudeTracker } from '../../../hooks/tracker';
import useChallengeLeaderboardAllQuery from '../../../hooks/use-challenge-leaderboard-all-query';
import useEventRankingQuery from '../../../hooks/use-event-ranking-query';
import { ExportIcon } from '../../icons';
import { fakeSites } from '../company-leaderboard/subgroups';
import FakeEmployees from '../individual-leaderboard/fake-employees';
import { LeaderboardIndividualItem } from '../individual-leaderboard/item';
import SitesLeaderboardItem from './item';
function SitesEventLeaderboard({
  event,
  parentId,
  isFree,
  disableLeaderboardRestriction,
  freeOfferEmployeesLeaderboardsMaxRowsCount,
  freeOfferSubgroupsLeaderboardsMaxRowsCount,
  FakeItemsCard,
  onExportReady
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const {
    t
  } = useTranslation();
  const {
    trackEvent
  } = useAmplitudeTracker();
  const {
    downloadXLSX
  } = useFileSaver();
  const isRestricted = isFree && !disableLeaderboardRestriction;
  const {
    data: rankingResult
  } = useEventRankingQuery(event.id, {
    page: 1,
    pageSize: 50,
    groupTypes: [],
    parentId,
    showSites: true
  });
  const items = rankingResult?.items;
  const {
    data: allLeaderboard,
    isLoading: loadingIndividual
  } = useChallengeLeaderboardAllQuery(selectedItem?.challengeId ?? undefined);
  const individualItems = allLeaderboard ? allLeaderboard.filter(({
    leaderboardIndex
  }) => leaderboardIndex !== undefined).map(({
    id,
    username,
    profilePicture,
    rank,
    leaderboardIndex,
    progressValue
  }) => ({
    rank,
    leaderboardIndex: leaderboardIndex,
    progressValue,
    user: id !== null ? {
      id,
      username: username || '',
      profilePicture: profilePicture ?? null
    } : null
  })) : undefined;
  const visibleIndividualItems = isRestricted ? individualItems?.slice(0, freeOfferEmployeesLeaderboardsMaxRowsCount) : individualItems;
  const visibleItems = isRestricted ? items?.slice(0, freeOfferSubgroupsLeaderboardsMaxRowsCount) : items;
  function handleExport() {
    if (!visibleIndividualItems?.length) return;
    trackEvent('Event Leaderboard Exported', {
      display_type: 'individual',
      event_id: event.id,
      format: 'xlsx'
    });
    downloadXLSX(`${[t('commons.events.details.leaderboard.title'), event.title, selectedItem?.title].join('_').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_')}_${moment().format('YYYY-MM-DD')}.xlsx`, [t('commons.events.details.leaderboard.rank'), t('commons.events.details.leaderboard.participant'), 'Score (pts)'], visibleIndividualItems.map(({
      rank,
      user,
      progressValue
    }) => [rank, user?.username ?? '', event.challengeTemplate?.type === 'traveledDistance' ? (progressValue / 1000).toFixed(1) : progressValue]));
  }
  function handleExportSites() {
    if (!visibleItems?.length) return;
    trackEvent('Event Leaderboard Exported', {
      display_type: 'companySite',
      event_id: event.id,
      format: 'xlsx'
    });
    downloadXLSX(`${[t('commons.events.details.leaderboard.title'), event.title].join('_').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '_')}_${moment().format('YYYY-MM-DD')}.xlsx`, [t('commons.events.details.leaderboard.rank'), t('commons.events.details.leaderboard.group'), 'Score (pts)'], visibleItems.map(({
      rank,
      title,
      progressValue
    }) => [rank, title, event.challengeTemplate?.type === 'traveledDistance' ? (progressValue / 1000).toFixed(1) : progressValue]));
  }
  useEffect(() => {
    if (!onExportReady) return;
    onExportReady(selectedItem ? visibleIndividualItems?.length ? handleExport : undefined : visibleItems?.length ? handleExportSites : undefined);
    return () => onExportReady(undefined);
  }, [onExportReady, selectedItem, visibleIndividualItems, visibleItems]);
  if (selectedItem) {
    return /*#__PURE__*/React.createElement(_Box, {
      display: "flex",
      flexDirection: "column",
      gap: 3,
      paddingY: 3
    }, /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      gap: 1,
      justifyContent: "space-between"
    }, /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      gap: 1
    }, /*#__PURE__*/React.createElement(_IconButton, {
      onClick: () => setSelectedItem(null),
      size: "small"
    }, /*#__PURE__*/React.createElement(_ChevronLeft, null)), /*#__PURE__*/React.createElement(_Typography, {
      fontWeight: 700
    }, selectedItem.title)), !onExportReady && visibleIndividualItems && visibleIndividualItems.length > 0 && /*#__PURE__*/React.createElement(_Button, {
      color: "primary",
      onClick: handleExport,
      startIcon: /*#__PURE__*/React.createElement(ExportIcon, null),
      sx: {
        textTransform: 'initial'
      },
      variant: "text"
    }, t('commons.actions.export'))), /*#__PURE__*/React.createElement(_Box, {
      bgcolor: "#fff",
      borderRadius: 4,
      display: "flex",
      flexDirection: "column",
      gap: 3,
      padding: 3,
      width: "100%"
    }, loadingIndividual ? /*#__PURE__*/React.createElement(_Box, {
      display: "flex",
      justifyContent: "center",
      paddingY: 5
    }, /*#__PURE__*/React.createElement(_CircularProgress, null)) : !individualItems || individualItems.length > 0 ? /*#__PURE__*/React.createElement(_Box, {
      border: "1px solid #dde0e7",
      borderRadius: 2,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      width: "100%"
    }, individualItems ? visibleIndividualItems?.map((item, index) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: item.user?.id ?? index
    }, /*#__PURE__*/React.createElement(_Divider, {
      sx: {
        borderColor: '#dde0e7'
      }
    }), /*#__PURE__*/React.createElement(LeaderboardIndividualItem, {
      challengeType: event.challengeTemplate?.type,
      item: item,
      prevItemRank: index > 0 ? visibleIndividualItems[index - 1].rank : null
    }))) : new Array(10).fill(null).map((_, index) => /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement(_Divider, {
      sx: {
        borderColor: '#dde0e7'
      }
    }), /*#__PURE__*/React.createElement(LeaderboardIndividualItem, {
      challengeType: event.challengeTemplate?.type,
      prevItemRank: null
    }))), isRestricted && individualItems && individualItems.length > freeOfferEmployeesLeaderboardsMaxRowsCount && /*#__PURE__*/React.createElement(FakeEmployees, {
      event: event,
      FakeItemsCard: FakeItemsCard,
      freeOfferEmployeesLeaderboardsMaxRowsCount: freeOfferEmployeesLeaderboardsMaxRowsCount
    })) : /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      gap: 3,
      paddingY: 5
    }, /*#__PURE__*/React.createElement("img", {
      height: 64,
      src: emptyStateIllustration
    }), /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      gap: 1
    }, /*#__PURE__*/React.createElement(_Typography, {
      align: "center",
      fontWeight: 700
    }, t('commons.challenges.leaderboard.empty_state_title_company')), /*#__PURE__*/React.createElement(_Typography, {
      align: "center",
      variant: "body2"
    }, t('commons.challenges.leaderboard.empty_state_description'))))));
  }
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingY: 3
  }, /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: 3,
    width: "100%"
  }, !onExportReady && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "h6"
  }, t('commons.challenges.navigation.leaderboard')), visibleItems && visibleItems.length > 0 && /*#__PURE__*/React.createElement(_Button, {
    color: "primary",
    onClick: handleExportSites,
    startIcon: /*#__PURE__*/React.createElement(ExportIcon, null),
    sx: {
      textTransform: 'initial'
    },
    variant: "text"
  }, t('commons.actions.export'))), !items || items.length > 0 ? /*#__PURE__*/React.createElement(_Box, {
    border: "1px solid #dde0e7",
    borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    width: "100%"
  }, items ? /*#__PURE__*/React.createElement(React.Fragment, null, visibleItems?.map((item, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: item.id
  }, index > 0 && /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(SitesLeaderboardItem, {
    challengeType: event.challengeTemplate?.type,
    item: item,
    onClick: () => setSelectedItem(item),
    prevItemRank: index > 0 ? visibleItems[index - 1].rank : null
  }))), isRestricted && items.length > freeOfferSubgroupsLeaderboardsMaxRowsCount && /*#__PURE__*/React.createElement(_Box, {
    sx: {
      position: 'relative'
    }
  }, fakeSites.map(({
    title
  }, index) => {
    const rank = freeOfferSubgroupsLeaderboardsMaxRowsCount + 2;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement(_Divider, {
      sx: {
        borderColor: '#dde0e7'
      }
    }), /*#__PURE__*/React.createElement(SitesLeaderboardItem, {
      blurred: true,
      challengeType: event.challengeTemplate?.type,
      item: {
        id: -1,
        activeParticipantsCount: 0,
        certified: false,
        challengeId: null,
        leaderboardIndex: rank + index,
        parentId: -1,
        parentPhoto: null,
        parentTitle: '',
        participantsCount: 0,
        rank,
        progressValue: 0,
        sizeRange: null,
        title,
        type: 'company',
        photo: null
      },
      prevItemRank: freeOfferSubgroupsLeaderboardsMaxRowsCount + 1
    }));
  }), /*#__PURE__*/React.createElement(FakeItemsCard, {
    isEventWithOffer: event
  }))) : new Array(10).fill(null).map((_, index) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: index
  }, index > 0 && /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7'
    }
  }), /*#__PURE__*/React.createElement(SitesLeaderboardItem, {
    challengeType: event.challengeTemplate?.type,
    prevItemRank: null
  })))) : /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingY: 5
  }, /*#__PURE__*/React.createElement("img", {
    height: 64,
    src: emptyStateIllustration
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700
  }, t('commons.challenges.leaderboard.empty_state_title_company')), /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    variant: "body2"
  }, t('commons.challenges.leaderboard.empty_state_description'))))));
}
export default SitesEventLeaderboard;